// The game's sprites on the page, drawn on the game's pixel grid. Every sprite
// pixel is a whole number of device pixels (`devicePixels`), and a nine-slice
// TILES its edges and middle the way Godot's NinePatchRect does, so wood grain
// and bezels never stretch into mixels. The art comes from the game through
// scripts/import-game-assets.mjs.

export interface Slice {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// A region of a sheet, in sprite pixels.
export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Game pixels to CSS pixels. The game draws at 2x on its own screen, and a
// phone's page is about as wide as half the game's, so 2 reads the same size.
export const ART_SCALE = 2;

const images = new Map<string, Promise<HTMLImageElement>>();

export function loadSprite(url: string): Promise<HTMLImageElement> {
  const kept = images.get(url);
  if (kept) return kept;
  const made = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error(`Could not load ${url}`)));
    img.src = url;
  });
  images.set(url, made);
  return made;
}

// Device pixels for each sprite pixel at `scale` CSS pixels per sprite pixel:
// always whole, so a 2.625 phone still draws every pixel the same size.
export const devicePixels = (scale: number): number =>
  Math.max(1, Math.round(scale * (window.devicePixelRatio || 1)));

// A canvas `w` by `h` sprite pixels, drawn at `scale`, its CSS size matched.
export function spriteCanvas(
  canvas: HTMLCanvasElement,
  w: number,
  h: number,
  scale: number,
): CanvasRenderingContext2D {
  const device = devicePixels(scale);
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, w * device);
  canvas.height = Math.max(1, h * device);
  canvas.style.width = `${canvas.width / ratio}px`;
  canvas.style.height = `${canvas.height / ratio}px`;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.imageSmoothingEnabled = false;
  context.setTransform(device, 0, 0, device, 0, 0);
  return context;
}

// One row of a nine-slice cell: the source strip tiled across `length`, the
// last tile cut short.
function tileRow(
  context: CanvasRenderingContext2D,
  img: HTMLImageElement,
  source: Region,
  at: { x: number; y: number },
  length: number,
): void {
  if (source.w <= 0) return;
  for (let run = 0; run < length; run += source.w) {
    const w = Math.min(source.w, length - run);
    context.drawImage(img, source.x, source.y, w, source.h, at.x + run, at.y, w, source.h);
  }
}

// Draws `region` of `img` nine-sliced into a box `w` by `h` sprite pixels at
// (x, y). The middle is tiled too, unless `hollow` leaves it empty.
export function nineSlice(
  context: CanvasRenderingContext2D,
  img: HTMLImageElement,
  region: Region,
  slice: Slice,
  box: Region,
  hollow = false,
): void {
  const { left, top, right, bottom } = slice;
  const midW = region.w - left - right;
  const midH = region.h - top - bottom;
  const innerW = box.w - left - right;
  const innerH = box.h - top - bottom;
  const sx = [region.x, region.x + left, region.x + region.w - right];
  const sy = [region.y, region.y + top, region.y + region.h - bottom];
  const dx = [box.x, box.x + left, box.x + box.w - right];
  const dy = [box.y, box.y + top, box.y + box.h - bottom];
  const widths = [left, midW, right];
  const heights = [top, midH, bottom];
  const spans = [left, innerW, right];
  const tall = [top, innerH, bottom];
  for (let row = 0; row < 3; row++) {
    for (let column = 0; column < 3; column++) {
      if (hollow && row === 1 && column === 1) continue;
      const source = {
        x: sx[column] as number,
        y: sy[row] as number,
        w: widths[column] as number,
        h: heights[row] as number,
      };
      const at = { x: dx[column] as number, y: dy[row] as number };
      const span = spans[column] as number;
      const height = tall[row] as number;
      if (source.h <= 0) continue;
      // A cell tiles across, then down.
      for (let down = 0; down < height; down += source.h) {
        const cut = Math.min(source.h, height - down);
        tileRow(context, img, { ...source, h: cut }, { x: at.x, y: at.y + down }, span);
      }
    }
  }
}
